import { useEffect, useState } from 'react'
import { HiInformationCircle, HiCheck } from 'react-icons/hi2'

import { TooltipBand } from './BandTooltip'
import { SelectSearch } from './SelectSearch'

import { SalaryComparativeTranslate } from '../../i18n/translatedMessages'

import SalaryBand from '../../API/Services/Salary/SalaryBand'

import Masks from '../../Utils/masks'
import {
	calculateSalaryDifference,
	calculatePercentageDifference,
	formatCurrency,
	getColorCurrentQuarter,
	calculateCurrentQuarter,
} from './Utils/Calc'

import SalaryComparativeChart from '../../Views/Services/Components/PayBalance/Components/SalaryComparativeChart'

export function Band({ employee }) {
	const [activeMydna, setActiveMydna] = useState(false)
	const [salaryInput, setSalaryInput] = useState('')
	const [band, setBand] = useState(null)

	useEffect(() => {
		if (employee.salaryBandByCompany.salary_band.id) {
			setBand(employee.salaryBandByCompany.salary_band.id)
		}
		setSalaryInput(employee.salaryBandByCompany.company_salary_mean)
	}, [employee])

	const handleSave = async () => {
		const save = {
			company_salary_mean: Number(Masks.removeDots(salaryInput)),
			salaryBand_id: band,
			employeeId: employee.employee_id,
		}

		employee.salaryBandByCompany.company_salary_id
			? await SalaryBand.updateSalaryCompany(employee.employee_id, save)
			: await SalaryBand.createSalaryCompany(save)
	}

	return (
		<div className='tw-w-full tw-bg-purple-50 tw-rounded-md tw-p-8 tw-px-6 tw-overflow-hidden'>
			<div className='tw-flex tw-items-center tw-gap-2 tw-relative'>
				<span className='tw-text-xl'>Comparativo Salarial</span>
				<div className='tw-bg-white tw-p-1 tw-overflow-hidden tw-rounded-full'>
					<button
						onClick={() => setActiveMydna(false)}
						className={`tw-rounded-full border-0 tw-px-3 tw-py-[2px]  ${
							!activeMydna ? 'tw-bg-purple tw-text-white' : 'tw-bg-white tw-text-gray-400 hover:tw-text-gray-600'
						}`}
					>
						Mi Empresa
					</button>
					<button
						onClick={() => setActiveMydna(true)}
						className={`tw-rounded-full border-0 tw-px-4 tw-py-[2px] ${
							activeMydna ? 'tw-bg-purple tw-text-white' : 'tw-bg-white tw-text-gray-400 hover:tw-text-gray-600'
						}`}
					>
						Mydna
					</button>
				</div>
				<TooltipBand color='#111827' className='tw-text-white'>
					<HiInformationCircle size={28} className='tw-text-purple cursor-pointer hover:tw-text-purple-400 ' />
				</TooltipBand>
			</div>
			<div className='tw-grid tw-grid-cols-2 tw-gap-4 '>
				{activeMydna ? (
					<MyDNABand employee={employee} />
				) : (
					<CompanyBand
						employee={employee}
						salaryInput={salaryInput}
						setSalaryInput={setSalaryInput}
						setBand={setBand}
						band={band}
						handleSave={handleSave}
					/>
				)}
				<div className='tw-py-8'>
					<div
						style={{
							backgroundColor: getColorCurrentQuarter(employee.monthly_salary, activeMydna ? employee.market_salary : Masks.removeDots(salaryInput)),
							color:
								getColorCurrentQuarter(employee.monthly_salary, activeMydna ? employee.market_salary : Masks.removeDots(salaryInput)) === '#d9d9f2'
									? '#444258'
									: '#ffffff',
						}}
						className='tw-w-full tw-h-[150px] tw-flex tw-flex-col tw-p-4 tw-rounded-xl '
					>
						<span>{SalaryComparativeTranslate.payrollStatus}</span>
						<span className='quartilStyle'>
							{calculateCurrentQuarter(employee.monthly_salary, activeMydna ? employee.market_salary : Masks.removeDots(salaryInput))}
						</span>
					</div>
				</div>
			</div>
			<div className='tw-w-full tw-mx-0'>
				<SalaryComparativeChart
					marketSalary={activeMydna ? employee.market_salary : Masks.removeDots(salaryInput)}
					currentSalary={employee.monthly_salary}
					formatCurrency={formatCurrency}
					typeCurrency={employee.currency}
				/>
			</div>
		</div>
	)
}

function MyDNABand({ employee }) {
	const { market_salary, monthly_salary, currency } = employee

	return (
		<div className='tw-w-full tw-grid tw-grid-cols-2 tw-mt-4'>
			<div className='tw-my-4 tw-space-y-2'>
				<h4 className='tw-text-base tw-text-gray-500'>{SalaryComparativeTranslate.midpointText}</h4>
				<span>{Masks.setDots(market_salary)}</span>
			</div>
			<div className='tw-my-4 tw-space-y-2'>
				<h4 className='tw-text-base tw-text-gray-500'>
					{SalaryComparativeTranslate.salaryDifferenceText} ({currency})
				</h4>
				<span>{calculateSalaryDifference(monthly_salary, market_salary, currency)}</span>
			</div>
			<div className='tw-my-4 tw-space-y-2'>
				<h4 className='tw-text-base tw-text-gray-500'>{SalaryComparativeTranslate.salaryColaborator}</h4>
				<span>{Masks.setDots(monthly_salary)}</span>
			</div>
			<div className='tw-my-4 tw-space-y-2'>
				<h4 className='tw-text-base tw-text-gray-500'>
					{SalaryComparativeTranslate.salaryDifferenceText}
					(%)
				</h4>
				<span>{calculatePercentageDifference(monthly_salary, market_salary, currency)}</span>
			</div>
		</div>
	)
}

function CompanyBand({ employee, salaryInput, setSalaryInput, band, setBand, handleSave }) {
	const { market_salary, monthly_salary, currency, country } = employee

	const handleCompanySalary = event => {
		setSalaryInput(event.target.value)
	}

	return (
		<div className='tw-w-full tw-mt-7'>
			<SelectSearch setSalary={setSalaryInput} setBand={setBand} bandId={band} countryId={country.id} />
			<div className='tw-w-full tw-grid tw-grid-cols-3'>
				<div className='tw-my-4 tw-space-y-2 tw-col-span-2'>
					<h4 className='tw-text-base tw-text-gray-500 tw-p-0 tw-m-0'>{SalaryComparativeTranslate.midpointText}</h4>
					<div className='tw-flex tw-items-center'>
						<input
							className='tw-m-0 tw-rounded-none border'
							style={{ borderRadius: 4 }}
							type='text'
							placeholder='Valor da Banda'
							value={salaryInput}
							onChange={handleCompanySalary}
							onFocus={() => {
								setSalaryInput('')
								setBand(null)
							}}
						/>
						<button
							onClick={handleSave}
							className='border-0 tw-bg-purple tw-text-white tw-rounded-md tw-h-[34px] tw-w-[34px] tw-ml-1 tw-flex tw-items-center tw-justify-center tw-shadow-md hover:tw-text-purple-200 active:tw-bg-purple active:tw-shadow-[inset_0_2px_2px_0px_rgba(255,255,255,0.4)] active:tw-border'
						>
							<HiCheck size={25} />
						</button>
					</div>
				</div>
				<div className='tw-my-4 tw-space-y-2'>
					<h4 className='tw-text-base tw-text-gray-500'>
						{SalaryComparativeTranslate.salaryDifferenceText} ({currency})
					</h4>
					{<span>{calculateSalaryDifference(monthly_salary, salaryInput, currency)}</span>}
				</div>
				<div className='tw-my-4 tw-space-y-2 tw-col-span-2'>
					<h4 className='tw-text-base tw-text-gray-500'>{SalaryComparativeTranslate.salaryColaborator}</h4>
					<span>{Masks.setDots(monthly_salary)}</span>
				</div>
				<div className='tw-my-4 tw-space-y-2'>
					<h4 className='tw-text-base tw-text-gray-500'>
						{SalaryComparativeTranslate.salaryDifferenceText}
						(%)
					</h4>
					<span>{calculatePercentageDifference(monthly_salary, salaryInput, currency)}</span>
				</div>
			</div>
		</div>
	)
}
